import { useRef, useState } from 'react';
import { useSongs } from '@/context/SongContext';
import { db, generateId } from '@/lib/db';
import { Button } from '@/components/ui/button';
import { Upload, FileAudio, FileText, Loader2, Check, File } from 'lucide-react';
import { toast } from 'sonner';

// Supported file types
const AUDIO_TYPES = ['.mp3', '.wav', '.m4a', '.ogg', '.webm', '.aac'];
const TEXT_TYPES = ['.txt', '.md'];

export default function FileImporter({ songId, onComplete }) {
    const { addFragment } = useSongs();
    const [importing, setImporting] = useState(false);
    const [importedFiles, setImportedFiles] = useState([]);
    const fileInputRef = useRef(null);

    const handleFileSelect = async (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;

        setImporting(true);
        const newImported = [];

        try {
            for (const file of files) {
                const ext = '.' + file.name.split('.').pop().toLowerCase();
                
                if (AUDIO_TYPES.includes(ext)) {
                    // Import audio file
                    const blobId = generateId();
                    await db.saveAudioBlob(blobId, file);
                    
                    // Get duration (approximate for some formats)
                    let duration = 0;
                    try {
                        const audio = new Audio();
                        audio.src = URL.createObjectURL(file);
                        await new Promise((resolve) => {
                            audio.onloadedmetadata = () => {
                                duration = audio.duration;
                                URL.revokeObjectURL(audio.src);
                                resolve();
                            };
                            audio.onerror = resolve;
                        });
                    } catch {}

                    await addFragment(songId, {
                        type: 'audio',
                        label: file.name.replace(/\.[^/.]+$/, ''), // Remove extension
                        blobId,
                        duration: Math.round(duration),
                        mimeType: file.type,
                        imported: true,
                        importedFrom: file.name
                    });
                    
                    newImported.push({ name: file.name, type: 'audio' });
                    
                } else if (TEXT_TYPES.includes(ext)) {
                    // Import text file
                    const content = await file.text();
                    
                    await addFragment(songId, {
                        type: 'text',
                        label: file.name.replace(/\.[^/.]+$/, ''),
                        content: content.trim(),
                        imported: true,
                        importedFrom: file.name
                    });
                    
                    newImported.push({ name: file.name, type: 'text' });
                    
                } else {
                    toast.error(`Unsupported file type: ${file.name}`);
                }
            }

            setImportedFiles(prev => [...prev, ...newImported]);
            
            if (newImported.length > 0) {
                toast.success(`Imported ${newImported.length} file${newImported.length !== 1 ? 's' : ''}`);
            }
            
        } catch (error) {
            console.error('Import error:', error);
            toast.error('Failed to import files');
        } finally {
            setImporting(false);
            // Reset input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleDone = () => {
        onComplete(null);
    };

    return (
        <div className="space-y-4 max-w-sm mx-auto">
            <p className="text-sm text-muted-foreground text-center">
                Import audio files (.mp3, .wav, .m4a) or text files (.txt, .md)
            </p>

            {/* File input */}
            <input
                ref={fileInputRef}
                type="file"
                accept={[...AUDIO_TYPES, ...TEXT_TYPES].join(',')}
                multiple
                onChange={handleFileSelect}
                className="hidden"
            />

            {/* Drop zone / select button */}
            <button
                onClick={() => fileInputRef.current?.click()}
                disabled={importing}
                className="w-full py-8 px-4 border-2 border-dashed border-border rounded-lg hover:border-primary/50 hover:bg-muted/30 transition-colors flex flex-col items-center gap-3"
            >
                {importing ? (
                    <Loader2 className="w-8 h-8 text-muted-foreground animate-spin" />
                ) : (
                    <Upload className="w-8 h-8 text-muted-foreground" />
                )}
                <span className="text-sm font-medium">
                    {importing ? 'Importing...' : 'Select files or drag & drop'}
                </span>
            </button>

            {/* Imported files list */}
            {importedFiles.length > 0 && (
                <div className="space-y-2">
                    <p className="text-xs text-muted-foreground font-medium">Imported:</p>
                    <div className="space-y-1.5">
                        {importedFiles.map((file, index) => (
                            <div 
                                key={index}
                                className="flex items-center gap-2 text-sm p-2 bg-muted/30 rounded"
                            >
                                {file.type === 'audio' ? (
                                    <FileAudio className="w-4 h-4 text-fragment-audio" />
                                ) : (
                                    <FileText className="w-4 h-4 text-fragment-text" />
                                )}
                                <span className="truncate flex-1">{file.name}</span>
                                <Check className="w-4 h-4 text-success" />
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Done button */}
            {importedFiles.length > 0 && (
                <Button onClick={handleDone} className="w-full">
                    Done
                </Button>
            )}

            {/* Supported formats */}
            <div className="text-xs text-muted-foreground text-center space-y-1">
                <p>Audio: mp3, wav, m4a, ogg, webm, aac</p>
                <p>Text: txt, md</p>
            </div>
        </div>
    );
}
